import React, { useEffect, useState } from 'react';
import { getAllClientReviewsByLocalId } from '../../../../crud/clients_api/client_reviews';
import { getUserMapByLocalId } from '../../../../crud/users_api/users';
import './ReviewsSummary.scss';
import { Button } from 'react-bootstrap';
import { ClientReview, UserMap } from './types';
import { fakeReviews, fakeUserMap } from './fakeReviews';

import LoadingSpinner from '../../../../components/common/LoadingSpinner';
import GeneralStats from './components/GeneralStats/GeneralStats';
import UserStats    from './components/UserStats/UserStats';
import ReviewsList  from './components/ReviewsList/ReviewsList';
import CompactFilterBar from './components/CompactFilterBar/CompactFilterBar';
import ReviewsOffCanvas from './components/OffCanvas/ReviewsOffCanvas';
import { useNotifications } from '../../../Base/components/NavBar/components/HeaderProfile/components/NotificationCenter/NotificationContext';
import { exportReviewsToExcel } from './utils/exportToExcel';
import { LocalSession } from '../../../../types/LocalSession';


interface ReviewsSummaryProps {
  lastSession?: LocalSession | null;
}

const ReviewsSummary: React.FC<ReviewsSummaryProps> = ({ lastSession }) => {
  // Sincronizar fechas con soloSesionActual y obtener session_start de la sesión activa
  const [selectedView, setSelectedView] = useState<'general' | 'usuarios' | 'listado'>('general');
  const [reviews, setReviews]     = useState<ClientReview[]>([]);
  const [userMap, setUserMap]     = useState<UserMap>({});
  const [loading, setLoading]     = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const today = new Date();
  // Por defecto: último día es hoy, inicio es hace un mes
  const oneMonthAgo = new Date(today);
  oneMonthAgo.setMonth(today.getMonth() - 1);
  const [startDate, setStartDate] = useState(oneMonthAgo.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);
  const [soloSesionActual, setSoloSesionActual] = useState(false);
  const notificationsCtx = useNotifications();


  /* 
  Setie el loadingSpinner como false inicialmente, para ir mapeando el error pero aun asi
  la vista permanece cargando, en el useEffect deje varios console.log para ver el estado del loading
  y ver si se estaba ejecutando el finally, que es donde se setea el loading

  A pesar de que el loading se setea a false en el finally, la vista no se actualiza

  En el back tambien esta devolviendo una lista vacia [] de reviews si no hay reviews, pero aun
  asi la vista permanece en loading
  
  */
  useEffect(() => {
    setLoading(true);
    
    // Check for demo mode
    const demoMode = localStorage.getItem('demoMode') === 'true';
    
    if (demoMode) {
      // Use fake data for demo mode
      setReviews(fakeReviews);
      setUserMap(fakeUserMap);
      setLoading(false);
      return;
    }
    
    // Regular mode - fetch real data
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const localId = Number(user.local_id) || 0;
    Promise.all([
      getAllClientReviewsByLocalId(localId),
      getUserMapByLocalId(localId),
    ])
      .then(([revRes, userRes]) => {
        setReviews(revRes.data ?? []);
        setUserMap(userRes.data ?? {});
      })
      .catch(err => {
        console.error('Error loading data', err);
      })
      .finally(() => {
        setLoading(false);
      });
}, []);


  // NOTE: removed automatic date-sync when toggling soloSesionActual.
  // We intentionally keep startDate/endDate under user control and apply
  // session filtering client-side (by session_id/local_session_id) so the
  // date inputs remain stable and predictable.

  // Helper de depuración manual (sin logs automáticos). Ejecutar en consola: window.__debugSessionMods()
  useEffect(() => {
    (window as any).__debugSessionMods = () => {
      const counts: Record<string, number> = {};
      reviews.forEach(r => {
        const sid = String((r as any).session_id ?? (r as any).local_session_id ?? 'none');
        counts[sid] = (counts[sid] || 0) + 1;
      });
      return counts;
    };
    return () => { delete (window as any).__debugSessionMods; };
  }, [reviews, lastSession, soloSesionActual]);

  useEffect(() => {
    if (notificationsCtx?.lastNotification?.topic === 'clientes') {
      // Check for demo mode
      const demoMode = localStorage.getItem('demoMode') === 'true';
      
      if (demoMode) {
        // In demo mode, just refresh with fake data
        setReviews(fakeReviews);
        setUserMap(fakeUserMap);
      } else {
        // Regular mode - refresh real data
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const localId = Number(user.local_id) || 0;
        Promise.all([
          getAllClientReviewsByLocalId(localId),
          getUserMapByLocalId(localId),
        ])
          .then(([revRes, userRes]) => {
            setReviews(revRes.data ?? []);
            setUserMap(userRes.data ?? {});
          })
          .catch(err => {
            console.error('Error loading data (notificación clientes)', err);
          });
      }
      // Limpiar la notificación para evitar loops
      notificationsCtx.setLastNotification(null);
    }
  }, [notificationsCtx]);

  // Handlers para desactivar soloSesionActual al cambiar fechas
  const handleStartDateChange = (newDate: string) => {
    setStartDate(newDate);
    if (endDate < newDate) {
      setEndDate(newDate);
    }
  };
  const handleEndDateChange = (newDate: string) => {
    setEndDate(newDate);
  };
  const handleSoloSesionActualChange = (checked: boolean) => {
    // Do not mutate dates when toggling session filter; apply session filter client-side
    setSoloSesionActual(checked);
  };

  // Handle Excel export
  const handleExportToExcel = () => {
    exportReviewsToExcel({
      reviews: filteredReviews,
      userMap: userMap,
      startDate: startDate,
      endDate: endDate
    });
  };

  // Filtrado de reviews por fecha y/o sesión actual.
  // If soloSesionActual is active and lastSession has an id, filter by session id (do not change dates).
  const filteredReviews = reviews.filter((r) => {
    // If session filter enabled and we have a session id, use id-based filter
    if (soloSesionActual && lastSession?.local_session_id) {
      const target = String(lastSession.local_session_id);
      const rid = String((r as any).session_id ?? (r as any).local_session_id ?? '');
      return rid === target;
    }

    // Otherwise use date range
    if (!r.timestamp) return false;
    const reviewDate = new Date(r.timestamp).toISOString().split('T')[0];
    if (startDate === '' && endDate === '') return true;
    return reviewDate >= startDate && reviewDate <= endDate;
  });

  // Active filter chips removed — CompactFilterBar provides the date controls

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="reviews-summary">
      <header className="mp-page-header-container">
        <h2 className='title'>Reseñas y Feedback</h2>
      </header>

      {/* Button Selector - Moved above filters */}
      <div className="mp-button-selector-page-container">
        <div className="mp-button-selector-page-selector">
          <Button 
            variant="none"
            onClick={() => setSelectedView('general')}
            className={`mp-button-selector-page-button ${selectedView === 'general' ? "active" : "inactive"}`}
          >
            Resumen
          </Button>
          <Button 
            variant="none"
            onClick={() => setSelectedView('usuarios')}
            className={`mp-button-selector-page-button ${selectedView === 'usuarios' ? "active" : "inactive"}`}
          >
            Por usuario
          </Button>
          <Button 
            variant="none"
            onClick={() => setSelectedView('listado')}
            className={`mp-button-selector-page-button ${selectedView === 'listado' ? "active" : "inactive"}`}
          >
            Listado
          </Button>
        </div>
      </div>

      {/* Compact Filter Bar */}
      <CompactFilterBar
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={handleEndDateChange}
        onShowMobileFilters={() => setShowFilters(true)}
        section={selectedView}
        soloSesionActual={soloSesionActual}
        onSoloSesionActualChange={handleSoloSesionActualChange}
        onExportToExcel={handleExportToExcel}
      />

  {/* Filtros activos removed — CompactFilterBar now provides the date controls */}
      {filteredReviews.length === 0 ? (
        <div style={{textAlign:'center', color:'var(--text-color)'}} className="no-reviews-msg">
          No existen datos para los filtros seleccionados{soloSesionActual ? ' (hoy)' : ''}.
        </div>
      ) : (
        <>
          {(selectedView === 'general') && (
            <GeneralStats
              reviews={filteredReviews}
              startDate={startDate}
              endDate={endDate}
            />
          )}
          {(selectedView === 'usuarios') && (
            <UserStats
              reviews={filteredReviews}
              userMap={userMap}
              startDate={startDate}
              endDate={endDate}
            />
          )}
          {(selectedView === 'listado') && <ReviewsList reviews={filteredReviews} userMap={userMap} />}
        </>
      )}

      <ReviewsOffCanvas
        show={showFilters}
        onHide={() => setShowFilters(false)}
        section={selectedView}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={handleEndDateChange}
        soloSesionActual={soloSesionActual}
        onSoloSesionActualChange={handleSoloSesionActualChange}
        onExportToExcel={handleExportToExcel}
      />
    </div>
  );
};

export default ReviewsSummary;
